/**
 * Repeat bookings on the client side (7 Oct 2026). Every visit of a repeat
 * is an ordinary booking carrying the same seriesId. The bookings list shows
 * a repeat as one row, and a visit that's different from the usual (another
 * time, day, stylist or service) is called out wherever it shows, so a
 * client used to "Wednesdays at 10" doesn't miss the week it's at 11:30.
 */
import { clientStartOf, fmtTime12, serviceLabelOf, stylistNameOf } from './bookingHelpers';

export const seriesIdOf = (b) => (b && b.seriesId ? String(b.seriesId) : null);

const minutesOf = (d) => d.getHours() * 60 + d.getMinutes();
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// The most common value (the first seen wins a tie)
const mode = (values) => {
  const counts = new Map();
  values.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  let best = null;
  let n = 0;
  counts.forEach((c, v) => { if (c > n) { best = v; n = c; } });
  return best;
};

// What a repeat usually is: its day, time, service and stylist
export const usualOf = (visits) => {
  const starts = visits.map(clientStartOf);
  return {
    day: mode(starts.map(d => d.getDay())),
    minutes: mode(starts.map(minutesOf)),
    service: mode(visits.map(serviceLabelOf)),
    stylist: mode(visits.map(stylistNameOf)),
  };
};

const timeSaid = (minutes) => {
  const d = new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60);
  return fmtTime12(d);
};

// How a visit differs from the usual, as short phrases ("at 11:30am instead
// of 10:00am"); empty when it's the same as always
export const differencesOf = (visit, usual) => {
  if (!visit || !usual) return [];
  const start = clientStartOf(visit);
  const out = []
  if (usual.day != null && start.getDay() !== usual.day) out.push(`on a ${DAYS[start.getDay()]} instead of a ${DAYS[usual.day]}`);
  if (usual.minutes != null && minutesOf(start) !== usual.minutes) out.push(`at ${fmtTime12(start)} instead of ${timeSaid(usual.minutes)}`);
  const stylist = stylistNameOf(visit);
  if (usual.stylist && stylist && stylist !== usual.stylist) out.push(`with ${stylist} instead of ${usual.stylist}`);
  const service = serviceLabelOf(visit);
  if (usual.service && service && service !== usual.service) out.push(`for ${service}`);
  return out;
};

// One line for a visit that's different: "Different time: at 11:30am
// instead of 10:00am"
export const differenceSaid = (diffs) => {
  if (!diffs || !diffs.length) return '';
  const what = diffs.length > 1 ? 'Different from usual'
    : /^at /.test(diffs[0]) ? 'Different time'
      : /^on a /.test(diffs[0]) ? 'Different day'
        : /^with /.test(diffs[0]) ? 'Different stylist' : 'Different service';
  return `${what}: ${diffs.join(', ')}`;
};

// "Every week", "Every 6 weeks", "Every month", from the gaps between visits
export const ruleOf = (visits) => {
  if (!visits || visits.length < 2) return '';
  const days = [];
  for (let i = 1; i < visits.length; i += 1) {
    const a = clientStartOf(visits[i - 1]);
    const b = clientStartOf(visits[i]);
    days.push(Math.round((new Date(b.getFullYear(), b.getMonth(), b.getDate()) - new Date(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000));
  }
  const gap = mode(days);
  if (gap >= 28 && gap <= 35 && days.some(d => d !== gap)) return 'Every month';
  if (gap % 7 === 0 && gap > 0) return gap === 7 ? 'Every week' : `Every ${gap / 7} weeks`;
  return 'Repeats';
};

// The repeat said the way a client thinks of it: "Every week, Wednesdays at 10:00am"
export const patternSaid = (visits) => {
  const usual = usualOf(visits);
  const rule = ruleOf(visits);
  const day = usual.day != null ? `${DAYS[usual.day]}s` : '';
  return [rule, [day, usual.minutes != null ? `at ${timeSaid(usual.minutes)}` : ''].filter(Boolean).join(' ')].filter(Boolean).join(', ');
};

// Upcoming bookings split into one entry per repeat (two or more visits
// with the same seriesId) and the rest, each list in date order
export const groupSeries = (list) => {
  const by = new Map();
  const singles = [];
  (list || []).forEach((b) => {
    const id = seriesIdOf(b);
    if (!id) { singles.push(b); return; }
    if (!by.has(id)) by.set(id, []);
    by.get(id).push(b);
  });
  const series = [];
  by.forEach((visits, id) => {
    if (visits.length < 2) { singles.push(...visits); return; }
    const sorted = [...visits].sort((a, b) => clientStartOf(a) - clientStartOf(b));
    const usual = usualOf(sorted);
    series.push({
      id,
      visits: sorted,
      usual,
      said: patternSaid(sorted),
      different: sorted.map(v => ({ visit: v, diffs: differencesOf(v, usual) })).filter(x => x.diffs.length),
    });
  });
  singles.sort((a, b) => clientStartOf(a) - clientStartOf(b));
  series.sort((a, b) => clientStartOf(a.visits[0]) - clientStartOf(b.visits[0]));
  return { singles, series };
};

// What the list worked out about each visit of a repeat, for the booking's
// own page (which loads one booking and can't see the others)
const known = new Map();
export const rememberSeries = (series) => {
  (series || []).forEach((x) => x.visits.forEach((v) => {
    const d = x.different.find(y => y.visit === v);
    known.set(String(v._id), { said: x.said, diffs: d ? d.diffs : [] });
  }));
};
export const seriesFor = (bookingId) => known.get(String(bookingId)) || null;
